import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const WhyChooseUs = () => {
  const theme = useTheme();

  const reasons = [
    {
      icon: <TrophyIcon sx={{ fontSize: 48 }} />,
      title: '10年专业经验',
      description: '深耕马来西亚留学市场十年，积累了丰富的申请经验和院校资源。',
      stats: '10年',
    },
    {
      icon: <VerifiedIcon sx={{ fontSize: 48 }} />,
      title: '98%成功率',
      description: '凭借专业的服务团队和完善的申请流程，保持极高的申请成功率。',
      stats: '98%',
    },
    {
      icon: <SchoolIcon sx={{ fontSize: 48 }} />,
      title: '30+合作院校',
      description: '与马来西亚排名前30的公立、私立大学建立直接合作关系，为学生提供更多选择。',
      stats: '30+',
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 48 }} />,
      title: '5000+成功案例',
      description: '已成功帮助超过5000名学生实现留学梦想，获得学生和家长一致好评。',
      stats: '5000+',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <Box sx={{ py: 8, backgroundColor: theme.palette.background.default }}>
      <Container maxWidth="xl">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* Section Header */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                mb: 2,
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.75rem' },
                color: theme.palette.text.primary,
              }}
            >
              为什么选择我们
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.secondary,
                maxWidth: 600,
                mx: 'auto',
                fontSize: { xs: '1rem', md: '1.2rem' },
              }}
            >
              专业、可靠、高效的留学服务，让您的留学之路更加顺畅
            </Typography>
          </Box>

          {/* Reasons Grid */}
          <Grid container spacing={4}>
            {reasons.map((reason, index) => (
              <Grid item xs={12} sm={6} lg={3} key={index}>
                <motion.div variants={itemVariants}>
                  <Card
                    sx={{
                      height: '100%',
                      textAlign: 'center',
                      p: 3,
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 0 }}>
                      {/* Icon */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 100,
                          height: 100,
                          borderRadius: '50%',
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          mx: 'auto',
                          mb: 3,
                        }}
                      >
                        {reason.icon}
                      </Box>

                      {/* Stats */}
                      <Typography
                        variant="h3"
                        sx={{
                          mb: 1,
                          fontWeight: 700,
                          background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}
                      >
                        {reason.stats}
                      </Typography>

                      {/* Title */}
                      <Typography
                        variant="h5"
                        sx={{
                          mb: 2,
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {reason.title}
                      </Typography>

                      {/* Description */}
                      <Typography
                        variant="body1"
                        sx={{
                          color: theme.palette.text.secondary,
                          lineHeight: 1.6,
                        }}
                      >
                        {reason.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default WhyChooseUs;
